import React, { Dispatch, SetStateAction, useState } from "react";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import Table, { TableProps } from "@awsui/components-react/table";
import Header from "@awsui/components-react/header";
import getLeagues from "api/getLeagues";
import { useQuery } from "react-query";
import useTable from "hooks/useTable";
import Pagination from "@awsui/components-react/pagination";
import TextFilter from "@awsui/components-react/text-filter";
import CollectionPreferences from "@awsui/components-react/collection-preferences";
import League from "model/League";
import JoinLeagueButton from "pages/leagues/components/JoinLeagueButton";
import { FlashbarProps } from "@awsui/components-react/flashbar";
import { useAuth } from "hooks/useAuth";
import Button from "@awsui/components-react/button";
import JoinLeagueModal from "pages/leagues/components/JoinLeagueModal";
import User from "model/User";
import Box from "@awsui/components-react/box";


const belongsToLeague = (user: User | undefined, league: League) => {
    return user?.leagues?.some(userLeague => userLeague.leagueId === league.leagueId) || false;
};

const columnDefinitions = (t: TFunction, tCommon: TFunction,
    setSubmitMessage: Dispatch<SetStateAction<FlashbarProps.MessageDefinition | undefined>>,
    setLeagueToJoin: Dispatch<SetStateAction<League | undefined>>, setShowJoinLeagueModal: Dispatch<SetStateAction<boolean>>,
    user: User | undefined): TableProps.ColumnDefinition<League>[] => [
        {
            id: "name",
            header: t("columns.name"),
            cell: (league: League) => league.name,
            sortingField: "name"
        },
        {
            id: "members",
            header: t("columns.members"),
            cell: (league: League) => league.members,
            sortingField: "members"
        },
        {
            id: "isPrivate",
            header: t("columns.private"),
            cell: (league: League) => league.isPrivate ? tCommon("yes") : tCommon("no"),
            sortingField: "isPrivate"
        },
        {
            id: "join",
            header: t("columns.join"),
            cell: (league: League) => belongsToLeague(user, league) ? (
                <Box variant="span">{t("alreadyMember")}</Box>
            ) : (
                league.isPrivate ? (
                    <Button
                        iconAlign="left"
                        variant="normal"
                        onClick={() => {
                            setLeagueToJoin(league);
                            setShowJoinLeagueModal(true);
                        }}
                    >
                        {t("joinButton")}
                    </Button>
                ) : (
                    <JoinLeagueButton
                        league={league}
                        setSubmitMessage={setSubmitMessage}
                        buttonEnabled={true}
                    />
                )
            ),
            sortingField: "join"
        }
    ];

interface LeaguesTableProps {
    setSubmitMessage: Dispatch<SetStateAction<FlashbarProps.MessageDefinition | undefined>>;
    showNewLeagueModal: boolean;
    setShowNewLeagueModal: Dispatch<SetStateAction<boolean>>;
    leagues?: League[];
    setLeagues: Dispatch<SetStateAction<League[] | undefined>>;
}

const LeaguesTable: React.FC<LeaguesTableProps> = (props) => {

    const {t} = useTranslation("leagues");
    const {t: tCommon} = useTranslation("common");
    const [{ user }] = useAuth();
    const [leagueToJoin, setLeagueToJoin] = useState<League>();
    const [showJoinLeagueModal, setShowJoinLeagueModal] = useState(false);
    const leagues = props.leagues;
    const setLeagues = props.setLeagues;

    const {
        isLoading: leaguesIsLoading,
        error: getLeaguesError
    } = useQuery<League[], Error>("getLeagues", () => getLeagues(user?.token), {
        onSuccess: (loadedLeagues: League[]) => {
            setLeagues(loadedLeagues);
        }
    });

    const {
        preferences,
        setPreferences,
        items,
        collectionProps,
        filterProps,
        paginationProps,
        collectionPreferencesProps
    } = useTable(leagues || [], {
        emptyStateSubtitle: getLeaguesError as Error,
        emptyStateTitle: t("empty"),
        pageSize: 10
    });

    return (
        <Table
            {...collectionProps}
            loadingText={t("loading")}
            columnDefinitions={columnDefinitions(t, tCommon, props.setSubmitMessage, setLeagueToJoin, setShowJoinLeagueModal, user)}
            items={items}
            loading={leaguesIsLoading}
            wrapLines={false}
            header={<Header>{t("allLeaguesTitle")}</Header>}
            pagination={
                <Pagination
                    {...paginationProps}
                    ariaLabels={{
                        nextPageLabel: tCommon("table.nextPage"),
                        previousPageLabel: tCommon("table.previousPage"),
                        pageLabel: pageNumber => tCommon("table.page", {page: pageNumber})
                    }}
                />
            }
            filter={
                <TextFilter
                    {...filterProps}
                    filteringPlaceholder={tCommon("table.filterByNamePlaceholder")}
                />
            }
            preferences={
                <CollectionPreferences
                    {...collectionPreferencesProps}
                    preferences={preferences}
                    onConfirm={({ detail }) => setPreferences(detail)}
                />
            }
            footer={
                <>
                    <Button
                        variant={"normal"}
                        iconName={"add-plus"}
                        onClick={() => props.setShowNewLeagueModal(!props.showNewLeagueModal)}
                    >
                        {t("newLeagueButton")}
                    </Button>
                    <JoinLeagueModal
                        league={leagueToJoin}
                        setLeague={setLeagueToJoin}
                        showModal={showJoinLeagueModal}
                        setShowModal={setShowJoinLeagueModal}
                    />
                </>
            }
        />
    );
};

export default LeaguesTable;
